import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { cn } from '@/lib/cn';

export type CustomerSectionTone = 'default' | 'primary' | 'warning' | 'success' | 'danger';

interface CustomerSectionCardProps {
  title?: string;
  /** One line under the title — what this section is for. */
  description?: string;
  icon?: IconDefinition;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** 'flat' = list/metadata rows, 'raised' = paper tier (default). */
  variant?: 'flat' | 'raised';
  /** Tints the icon tile and adds a left accent — for the one section that
   * currently needs the customer's attention. */
  tone?: CustomerSectionTone;
  /** Removes inner padding so lists can run edge to edge. */
  flush?: boolean;
  id?: string;
  'data-testid'?: string;
}

const TONE_TILE: Record<CustomerSectionTone, string> = {
  default: 'bg-customer-text/[0.06] text-customer-text-secondary',
  primary: 'bg-customer-primary/10 text-customer-primary-text',
  warning: 'bg-customer-warning/15 text-customer-warning-text',
  success: 'bg-customer-success/15 text-customer-success-text',
  danger: 'bg-customer-danger/10 text-customer-danger-text',
};

const TONE_ACCENT: Record<CustomerSectionTone, string> = {
  default: '',
  primary: 'border-l-[3px] border-l-customer-primary',
  warning: 'border-l-[3px] border-l-customer-warning',
  success: 'border-l-[3px] border-l-customer-success',
  danger: 'border-l-[3px] border-l-customer-danger',
};

/**
 * The one section container for customer screens. Mobile-first padding
 * (16px, 20px from `sm`), a header row with an icon tile, title and optional
 * description, and an optional tone accent so the section that needs a
 * decision is visibly different from the ones that merely inform.
 */
export function CustomerSectionCard({
  title,
  description,
  icon,
  actions,
  children,
  className = '',
  variant = 'raised',
  tone = 'default',
  flush = false,
  id,
  'data-testid': testId,
}: CustomerSectionCardProps) {
  const hasHeader = Boolean(title || actions);
  return (
    <section
      id={id}
      data-testid={testId}
      className={cn(
        'rounded-customer border border-customer-border bg-customer-surface',
        variant === 'raised' ? 'shadow-customer-sm' : '',
        TONE_ACCENT[tone],
        flush ? 'overflow-hidden' : 'p-4 sm:p-5',
        className,
      )}
    >
      {hasHeader && (
        <div
          className={cn(
            'flex items-start justify-between gap-3',
            flush ? 'px-4 pt-4 sm:px-5 sm:pt-5' : '',
            'mb-4',
          )}
        >
          {title && (
            <div className="flex min-w-0 items-center gap-3">
              {icon && (
                <span
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-customer-sm',
                    TONE_TILE[tone],
                  )}
                  aria-hidden="true"
                >
                  <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5" />
                </span>
              )}
              <div className="min-w-0">
                <h2 className="text-[15px] font-semibold leading-5 text-customer-text">{title}</h2>
                {description && (
                  <p className="mt-0.5 text-xs leading-4 text-customer-text-secondary">
                    {description}
                  </p>
                )}
              </div>
            </div>
          )}
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}
